import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle, Image as ImageIcon, FileText, Eye, Sparkles } from "lucide-react";

const MODULE_OPTIONS = [
  { value: "bang_ke_tien_mat", label: "Bảng kê tiền mặt" },
  { value: "bang_ke_doanh_thu", label: "Doanh thu Pippy" },
  { value: "doanh_thu_ngay", label: "Doanh thu ngày" },
  { value: "bao_cao_kd", label: "Báo cáo kinh doanh" },
  { value: "so_thu_chi_chung_tu", label: "Sổ thu chi — Chứng từ" },
  { value: "so_thu_chi_so_quy", label: "Sổ thu chi — Sổ quỹ" },
  { value: "so_thu_chi", label: "Sổ thu chi" },
];

const MODULE_LABEL = {
  bang_ke_tien_mat: "Bảng kê tiền mặt",
  bang_ke_doanh_thu: "Doanh thu Pippy",
  doanh_thu_ngay: "Doanh thu ngày",
  bao_cao_kd: "Báo cáo kinh doanh",
  so_thu_chi: "Sổ thu chi",
  chung_tu: "Chứng từ",
};

function resolveModuleAndSheet(value) {
  if (value === "so_thu_chi_chung_tu") return ["so_thu_chi", "chung_tu"];
  if (value === "so_thu_chi_so_quy") return ["so_thu_chi", "so_quy"];
  return [value, null];
}

export default function PendingReviewRow({ item, onConfirm, onDismiss, onView, onSaveAsSample, selected, onToggleSelect }) {
  // Map AI suggestion → composite value
  let suggestedValue = item.target_module || "";
  if (item.target_module === "so_thu_chi" && item.sheet_type === "chung_tu") suggestedValue = "so_thu_chi_chung_tu";
  else if (item.target_module === "so_thu_chi" && item.sheet_type === "so_quy") suggestedValue = "so_thu_chi_so_quy";

  const [moduleValue, setModuleValue] = useState(suggestedValue || "bang_ke_tien_mat");
  const [dateValue, setDateValue] = useState(item.date_guess || "");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    const [tm, st] = resolveModuleAndSheet(moduleValue);
    setBusy(true);
    try {
      const result = await onConfirm(item.id, tm, st, dateValue || null);
      // Closed-loop: nếu là ảnh → đề xuất lưu thành mẫu
      const isImg = (item.mime_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(item.file_name);
      if (isImg && result?.savedSuccessfully && onSaveAsSample) {
        onSaveAsSample({
          attachment_id: item.attachment_id,
          target_module: tm,
          sheet_type: st,
          file_name: item.file_name,
          // Note: import_id đã bị xoá ở backend confirm flow, nên cần dùng attachment_id để re-create sample
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss(item.id);
    } finally {
      setBusy(false);
    }
  };

  const isImage = (item.mime_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(item.file_name);
  const isPdf = (item.mime_type || "") === "application/pdf" || /\.pdf$/i.test(item.file_name);

  const confidencePct = item.ai_confidence ? Math.round(item.ai_confidence * 100) : 0;

  return (
    <div
      className={`bg-[#0f172a] border rounded-lg p-3 space-y-2 transition-colors ${
        selected ? "border-indigo-400 ring-1 ring-indigo-400/40" : "border-amber-500/30"
      }`}
      data-testid={`pending-review-row-${item.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(item.id)}
            className="mt-2 w-4 h-4 accent-indigo-500 cursor-pointer shrink-0"
            data-testid={`pending-review-checkbox-${item.id}`}
            title="Chọn để xác nhận hàng loạt"
          />
        )}
        {/* Icon */}
        <div className="w-9 h-9 rounded bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
          {isImage ? <ImageIcon className="w-4 h-4 text-amber-400" /> : <FileText className="w-4 h-4 text-amber-400" />}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onView(item.attachment_id)}
              className="text-xs font-medium text-slate-200 hover:text-indigo-300 truncate max-w-[260px] text-left"
              data-testid={`pending-review-view-${item.id}`}
              title="Xem file"
            >
              {item.file_name}
            </button>
            {(isImage || isPdf) && (
              <Badge className="bg-slate-500/15 text-slate-400 border border-slate-500/30 text-[9px]">
                <Eye className="w-2.5 h-2.5 mr-1 inline" />
                {isImage ? "Ảnh" : "PDF"}
              </Badge>
            )}
            {item.ai_confidence != null && (
              <Badge
                className={`text-[9px] border ${
                  confidencePct >= 50
                    ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                    : "bg-red-500/15 text-red-400 border-red-500/30"
                }`}
                title={item.ai_reason}
              >
                <Sparkles className="w-2.5 h-2.5 mr-1 inline" />
                AI {confidencePct}%
              </Badge>
            )}
            {item.target_module && item.target_module !== "unknown" && (
              <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[9px]">
                Gợi ý: {MODULE_LABEL[item.target_module] || item.target_module}
              </Badge>
            )}
          </div>
          {item.ai_reason && (
            <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">{item.ai_reason}</p>
          )}
          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-600">
            <span>#{item.channel_name || "—"}</span>
            <span>· {item.author_name || "—"}</span>
            <span>· {item.discord_message_timestamp ? item.discord_message_timestamp.slice(0, 10) : ""}</span>
          </div>
        </div>
      </div>

      {/* Action row */}
      <div className="flex items-end gap-2 flex-wrap pl-12">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] text-slate-500 block mb-0.5">Module đích</label>
          <Select value={moduleValue} onValueChange={setModuleValue}>
            <SelectTrigger
              className="h-8 bg-[#1e293b] border-[#334155] text-slate-200 text-xs"
              data-testid={`pending-review-module-${item.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1e293b] border-[#334155]">
              {MODULE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-slate-300 text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[140px]">
          <label className="text-[10px] text-slate-500 block mb-0.5">Ngày (YYYY-MM-DD)</label>
          <input
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            className="w-full h-8 bg-[#1e293b] border border-[#334155] text-slate-200 text-xs rounded-md px-2"
            data-testid={`pending-review-date-${item.id}`}
          />
        </div>
        <Button
          onClick={handleConfirm}
          disabled={busy}
          className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs px-3"
          data-testid={`pending-review-confirm-${item.id}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Xác nhận import
        </Button>
        <Button
          onClick={handleDismiss}
          disabled={busy}
          variant="ghost"
          className="h-8 text-slate-500 hover:text-red-400 hover:bg-red-500/10 text-xs px-2"
          data-testid={`pending-review-dismiss-${item.id}`}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" /> Bỏ qua
        </Button>
      </div>
    </div>
  );
}
